import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"
import { ReserveApplyCodePOSTSuccessResponse } from "ipctg-inflow-types/InFlowReserveAPI"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import StatusToast from "@/globalComponents/atoms/StatusToast"
import TextField from "@/globalComponents/molecules/TextField"
import { ExpectedError } from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { log } from "@/utilities/logging"
import { normalizeAccessCode } from "@/utilities/normalizers"
import { TranslationKey } from "@/utilities/types/local"

import styles from "./index.module.css"

// Module-level counter so each toast gets a stable, unique key independent of
// render state. Deriving the id from the previous toast can collide with a
// node still animating out (e.g. dismiss then immediately re-submit).
let toastCount = 0
function genToastId() {
  toastCount = (toastCount + 1) % Number.MAX_SAFE_INTEGER
  return toastCount
}

type ToastMessage =
  | {
      ns: "common"
      key: TranslationKey<"common">
      values?: Record<string, string>
    }
  | {
      ns: "errors"
      key: TranslationKey<"errors">
      values?: Record<string, string>
    }

type ToastState = {
  id: number
  message: ToastMessage
  variant: "success" | "warning" | "error"
} | null

interface FormSchema {
  key: string
}

interface Props {
  mutationOptions: UseMutationOptions<
    ReserveApplyCodePOSTSuccessResponse,
    Error,
    string
  >
}

export default function EnterAppUserGroupForm({ mutationOptions }: Props) {
  const { t } = useTranslation(["common", "errors"])
  const [toast, setToast] = useState<ToastState>(null)

  const form = useForm<FormSchema>({
    defaultValues: { key: "" },
    mode: "onBlur",
  })

  const { mutate, isPending } = useMutation({
    ...mutationOptions,
    onSuccess: (data, vars, ctx) => {
      handleSuccess(data)
      mutationOptions.onSuccess?.(data, vars, ctx)
    },
    onError: (err, vars, ctx) => {
      handleError(err)
      mutationOptions.onError?.(err, vars, ctx)
    },
  })

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <fieldset disabled={isPending} className={styles.wrapper}>
        <div className={styles.inputRow}>
          <Controller
            name="key"
            control={form.control}
            rules={{
              required: requiredRule(t("enterAppUserGroupForm.required")),
            }}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onChange={(value) => onChange(normalizeAccessCode(value))}
                isInvalid={invalid}
                className={styles.field}
                aria-label={t("enterAppUserGroupForm.placeholder")}
              >
                <TextField.Input
                  innerRef={ref}
                  placeholder={t("enterAppUserGroupForm.placeholder")}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Button
            buttonType="primary"
            type="submit"
            isPending={isPending}
            className={styles.addButton}
          >
            {t("enterAppUserGroupForm.addButton")}
          </Button>
        </div>
        {toast && (
          <StatusToast
            key={toast.id}
            message={getToastMessage(toast.message)}
            variant={toast.variant}
            cancellable
            onDismiss={() => setToast(null)}
          />
        )}
      </fieldset>
    </form>
  )

  function onSubmit(data: FormSchema) {
    setToast(null)
    mutate(data.key.trim())
  }

  function showToast(
    message: ToastMessage,
    variant: NonNullable<ToastState>["variant"]
  ) {
    setToast({ id: genToastId(), message, variant })
  }

  function handleSuccess(result: ReserveApplyCodePOSTSuccessResponse) {
    form.reset()

    showToast(
      result.userFacingRedeemCopy
        ? {
            ns: "common",
            key: "enterAppUserGroupForm.adminSuppliedCopy",
            values: {
              adminSuppliedCopy: result.userFacingRedeemCopy,
            },
          }
        : {
            ns: "common",
            key: "enterAppUserGroupForm.success",
          },
      "success"
    )
  }

  function handleError(err: Error) {
    log({
      level: "warn",
      flow: "misc",
      scope: "validation.appUserGroup",
      message: "Failed to add access key",
      meta: { error: err },
      sentry: err instanceof ExpectedError ? "breadcrumb" : "exception",
      exception: err,
    })

    if (err instanceof ExpectedError) {
      showToast({ ns: "errors", key: err.translationKey }, "warning")
      return
    }

    showToast({ ns: "common", key: "enterAppUserGroupForm.error" }, "error")
  }

  function getToastMessage(message: ToastMessage) {
    return message.ns === "errors"
      ? t(message.key, { ns: "errors", ...message.values })
      : t(message.key, { ns: "common", ...message.values })
  }
}
